import { Drawer } from 'components/Drawer'
import { useState } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { filterPrivateWorkspaces } from 'utils/workspaceUtils'
import { WorkspaceSelector } from './components/WorkspaceSelector/WorkspaceSelector'
import Styles from './LocationPicker.module.scss'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { LocationPickerFolder, SelectedFolder } from './LocationPicker'
import { useLocationPicker } from './hooks/useLocationPicker'
import { ActionType } from './LocationPickerModal'
import { LocationPickerContent } from './LocationPickerContent'
import { DrawerProps } from 'components/Drawer/Root'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

interface Props extends DrawerProps {
  actionType: ActionType
  initialFolder: LocationPickerFolder | null | undefined
  onSelectFolder: ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: SelectedFolder | null
    newWorkspaceId: number
  }) => void
  initialWorkspaceId?: number
  isRootDisabled: boolean
  disabledFolderIds?: string[]
  isWorkspaceSelectorEnabled?: boolean
  fileTitle?: string
}

export const LocationPickerDrawer = observer(
  ({
    actionType,
    initialFolder,
    onSelectFolder,
    initialWorkspaceId,
    isRootDisabled = true,
    disabledFolderIds = [],
    isWorkspaceSelectorEnabled = false,
    fileTitle,
    ...props
  }: Props) => {
    const { drawersStore } = useRootStore()
    const close = () => drawersStore.closeDrawer()
    const { setActiveFolder, searchPlaceholder } = useLocationPicker({
      actionType,
      initialFolder,
      onSelectFolder,
      initialWorkspaceId,
      fileTitle,
    })
    const workspaces = useWorkspaces()
    const currentWorkspaceId = useCurrentWorkspaceId()
    const privateWorkspaces = filterPrivateWorkspaces(workspaces)
    const [workspaceId, setWorkspaceId] = useState(initialWorkspaceId || currentWorkspaceId)

    return (
      <Drawer.NestedRoot {...props}>
        <Drawer.Portal>
          <Drawer.Content fullHeight color="var(--light-40)">
            <Drawer.InnerContent isSnappedTop>
              <Drawer.Top>
                <Drawer.Close />
                <Drawer.Title asChild>
                  <Heading level={5}>{searchPlaceholder}</Heading>
                </Drawer.Title>
                <div className={Styles.workspaceSelector}>
                  {isWorkspaceSelectorEnabled && (
                    <WorkspaceSelector
                      workspaces={privateWorkspaces}
                      workspaceId={workspaceId}
                      isIcon
                      onWorkspaceSelect={(id: number) => {
                        setActiveFolder(null)
                        setWorkspaceId(id)
                      }}
                    />
                  )}
                </div>
              </Drawer.Top>
              <Drawer.Container>
                <LocationPickerContent
                  actionType={actionType}
                  initialFolder={initialFolder}
                  onSelectFolder={onSelectFolder}
                  initialWorkspaceId={initialWorkspaceId}
                  isRootDisabled={isRootDisabled}
                  disabledFolderIds={disabledFolderIds}
                  fileTitle={fileTitle}
                  workspaceId={workspaceId}
                  onConfirm={close}
                />
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.NestedRoot>
    )
  },
)
